import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { dispatcher } from 'dispatcher';
import { pick, prop } from 'ramda';

import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

import { useGtmEvent } from 'js/app/hooks/analiticsSubscribe';
import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { useGetServerTime } from 'js/app/hooks/useServerTime';
import { setUrlRedirectBackAfterDeposit } from 'js/components/system/App/store/buzz/actions';
import { useNavigate } from 'js/hooks/navigate';
import { useGetPaymentStatistics } from 'js/hooks/user';
import { storageService } from 'js/services/storage.service';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

const handleSCMessages = (data, options) => {
    const {
        urlRedirectBackAfterDeposit,
        getServerTime,
        userDetails = {},
        gtmEvent,
        redirectBackAfterDeposit,
        getPaymentStatistics,
        dTagPaymentStatisticUpdateDelay,
        clientSkin = ''
    } = options;

    let eventData;
    let appEvent;

    try {
        eventData = JSON.parse(data);
    } catch (e) {
        eventData = false;
    }

    if (!eventData) {
        return;
    }

    const {
        action: eventDataAction,
        ...gtmData
    } = eventData;

    if (eventData.action && eventData.payload) {
        const currentTimestamp = getServerTime();
        const signupTimeStamp = new Date(`${(String(userDetails.signupDate) || '').replace(' ', 'T')}Z`).getTime();
        const isImmediate = signupTimeStamp + 86400000 > currentTimestamp;
        const {
            action: payloadAction,
            ...gtmDataPayload
        } = eventData.payload;

        if (payloadAction === 'made first deposit') {
            gtmEvent(isImmediate ? 'immediate_ftd' : 'non_immediate_ftd', gtmData);

            appEvent = 'firstsuccessdeposit';
        }

        dispatcher.dispatch('safecharge:analytics', eventData.payload);

        if (payloadAction === 'made deposit' || payloadAction === 'made first deposit') {
            const depositAmount = parseFloat(eventData.payload?.deposit_amount) || 0;

            setTimeout(() => {
                getPaymentStatistics().then(({ deposits } = {}) => {
                    gtmEvent(
                        'safecharge_deposit',
                        {
                            ...gtmDataPayload,
                            // eslint-disable-next-line camelcase
                            user_num_deposits: parseFloat(deposits?.totalDepositCount),
                            // eslint-disable-next-line camelcase
                            user_deposit_currency: eventData.payload?.currency,
                            // eslint-disable-next-line camelcase
                            user_deposit_amount: depositAmount,
                            // eslint-disable-next-line camelcase
                            user_total_deposit_amount: parseFloat(deposits?.totalDepositAmount),
                            // eslint-disable-next-line camelcase
                            user_total_deposit_count: parseFloat(deposits?.totalDepositCount)
                        }
                    );

                    gtmEvent('purchase', {
                        ecommerce: {
                            currency: eventData.payload?.currency,
                            value: depositAmount,
                            // eslint-disable-next-line camelcase
                            transaction_id: eventData.payload.reference,
                            items: [{
                                // eslint-disable-next-line camelcase
                                item_name: 'Deposit',
                                // eslint-disable-next-line camelcase
                                item_id: 'deposit',
                                // eslint-disable-next-line camelcase
                                item_brand: clientSkin.includes('buzzbingo') ? 'Buzz Bingo' : 'Buzz Casino',
                                // eslint-disable-next-line camelcase
                                item_category: payloadAction === 'made first deposit' ? 'ftd' : 'std',
                                quantity: '1'
                            }],
                            // eslint-disable-next-line camelcase
                            first_name: userDetails.firstName,
                            // eslint-disable-next-line camelcase
                            last_name: userDetails.lastName,
                            // eslint-disable-next-line camelcase
                            email_address: userDetails.email,
                            city: userDetails.city,
                            county: userDetails.state,
                            postcode: userDetails.zip,
                            // eslint-disable-next-line camelcase
                            phone_number: userDetails.phone
                        }
                    });
                });
            }, Number(dTagPaymentStatisticUpdateDelay));
        } else {
            gtmEvent(
                `safecharge_${payloadAction ? payloadAction.replaceAll(' ', '_') : eventData.action}`,
                gtmDataPayload
            );
        }
    } else if (eventData.action && !eventData.payload) {
        gtmEvent(`safecharge_${eventData.action.replaceAll(' ', '_')}`, gtmData);
    } else if (eventData.payload && !eventData.action) {
        gtmEvent(`safecharge_${eventData.payload.replaceAll(' ', '_')}`, gtmData);
    } else if (eventData === 'success') {
        if (urlRedirectBackAfterDeposit) {
            redirectBackAfterDeposit();
        }
    }

    if (appEvent && storageService.session.getItem('isNative')) {
        window.location.href = `htcmd:${appEvent}`;
    }
};

const postMessages = (options) => (event) => {
    const origin = event?.origin || event?.originalEvent?.origin;

    if (origin.endsWith('safecharge.com')) {
        handleSCMessages(event.data, options);
    }
};

export const useCashierMessages = () => {
    const { urlRedirectBackAfterDeposit, clientSkin } = useAppSelector(pick(['clientSkin', 'urlRedirectBackAfterDeposit']));
    const userDetails = useUserSelector(prop('userDetails'));
    const [dTagPaymentStatisticUpdateDelay = 1000] = useDynamicTags([DYNAMIC_TAG_NAMES.paymentStatisticUpdateDelay]);
    const getServerTime = useGetServerTime();
    const gtmEvent = useGtmEvent();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const getPaymentStatistics = useGetPaymentStatistics(false);

    const redirectBackAfterDeposit = useCallback(() => {
        dispatch(setUrlRedirectBackAfterDeposit(''));
        navigate(urlRedirectBackAfterDeposit);
    }, [dispatch, navigate, urlRedirectBackAfterDeposit]);

    return useEffect(() => {
        const handlerPostMessages = postMessages({
            urlRedirectBackAfterDeposit,
            getServerTime,
            userDetails,
            gtmEvent,
            redirectBackAfterDeposit,
            getPaymentStatistics,
            dTagPaymentStatisticUpdateDelay,
            clientSkin
        });

        window.addEventListener('message', handlerPostMessages);

        return () => {
            window.removeEventListener('message', handlerPostMessages);
        };
    }, [
        gtmEvent,
        getServerTime,
        redirectBackAfterDeposit,
        urlRedirectBackAfterDeposit,
        userDetails,
        getPaymentStatistics,
        dTagPaymentStatisticUpdateDelay,
        clientSkin
    ]);
};
